import React, { useMemo } from "react";
import Select from "react-dropdown-select";
import "./CustomDropdown.css";

const CustomDropdown = ({
   label,
   optionData = [],
   onChange,
   name,
   values,
   disabled = false,
   errors,
   message,
   require,
   multiple = false,
   labelField = "label",
   valueField = "value",
   value,
   placeholder,
}) => {
   // ✅ Memoized selected values (prevents unnecessary recalculation)
   const selectedValues = useMemo(() => {
      // If already controlled via `values` (multi-select support)
      if (values && Array.isArray(values)) {
         return values;
      }

      // If single value is passed
      if (value !== undefined && value !== null) {
         return optionData.filter((item) => item?.[valueField] === value);
      }

      return [];
   }, [values, value, optionData, valueField]);

   // ✅ Handle change safely for both single & multi
   const handleChange = (data) => {
      if (!onChange) return;

      // ✅ When cleared (backspace or remove)
      if (!data || data.length === 0) {
         if (multiple) {
            onChange([], []); // multi
         } else {
            onChange([], null); // single
         }
         return;
      }

      if (multiple) {
         const extractedValues = data.map((item) => item[valueField]);
         onChange(data, extractedValues);
      } else {
         onChange(data, data[0]?.[valueField] ?? null);
      }
   };

   // ✅ Custom Content Renderer for "Perfect Design"
   const customContentRenderer = ({ props, state, methods }) => {
      return (
         <div className="d-flex flex-wrap align-items-center w-100">
            {state.values.map((item) => (
               <div key={item[props.valueField]} className="custom-dropdown-pill">
                  {item[props.labelField]}
                  {!disabled && (
                     <button
                        type="button"
                        className="custom-dropdown-pill-close"
                        onClick={(e) => {
                           e.stopPropagation();
                           methods.removeItem(e, item);
                        }}
                     >
                        &times;
                     </button>
                  )}
               </div>
            ))}
            {!disabled && (
               <input
                  type="text"
                  className="custom-dropdown-input"
                  placeholder={state.values.length > 0 ? "" : placeholder || "Select..."}
                  value={state.search}
                  onChange={methods.setSearch}
                  onKeyDown={(e) => {
                     // ❌ Not allow to backspace to delete selection
                     if (e.key === "Backspace" && !state.search) {
                        e.preventDefault();
                     }
                  }}
               />
            )}
         </div>
      );
   };

   return (
      <div
         className={`form-group custom-dropdown-container ${disabled ? "disabled" : ""}`}
      >
         {label && (
            <label className="text-capitalize">
               {label}
               {require && <span className="text-danger">*</span>}
            </label>
         )}

         <Select
            options={optionData}
            labelField={labelField}
            valueField={valueField}
            name={name}
            values={selectedValues}
            onChange={handleChange}
            searchable
            multi={multiple}
            disabled={disabled}
            searchBy={labelField}
            backspaceDelete={false} // ✅ Explicitly disable backspace delete
            contentRenderer={customContentRenderer}
            placeholder={placeholder}
            className="react-dropdown-select-custom fix-width"
            style={{
               backgroundColor: disabled ? "#e9ecef" : "#fff",
            }}
         />

         {errors && (
            <div
               className="text-danger mt-1"
               style={{ fontSize: ".8rem", fontWeight: "500" }}
            >
               {message}
            </div>
         )}
      </div>
   );
};

export default CustomDropdown;
